import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DocsContentService } from './services/docs-content.service';
import { DocsThemeService } from './services/docs-theme.service';
import { NAV_ICONS, NAV_ICON_FALLBACK } from './docs-icons';
import {
  Manifest,
  MenuItem,
  NavSection,
  OutlineItem,
  SearchResult,
} from './models/docs.models';

@Component({
  selector: 'app-docs',
  standalone: true,
  imports: [RouterModule, FormsModule],
  templateUrl: './docs.component.html',
  styleUrls: ['./docs.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DocsContentService, DocsThemeService],
})
export class DocsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly content = inject(DocsContentService);
  private readonly themeService = inject(DocsThemeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  // Constants
  readonly DEFAULT_SLUG = 'introductie';

  // Signals for state management
  manifest = signal<Manifest | null>(null);
  flatItems = signal<MenuItem[]>([]);
  currentSlug = signal<string>(this.DEFAULT_SLUG);
  currentArticleHtml = signal<SafeHtml>('');
  breadcrumbs = signal<string[]>([]);
  outlineItems = signal<OutlineItem[]>([]);
  activeOutlineId = signal<string>('');
  readonly theme = this.themeService.theme;
  lightboxImage = signal<string | null>(null);
  lightboxAlt = signal<string | null>(null);
  lightboxOpen = signal<boolean>(false);

  // Search
  searchQuery = signal<string>('');
  searchResults = signal<SearchResult[]>([]);
  showSearchResults = signal<boolean>(false);
  private objectUrls: string[] = [];
  private readonly navigationDestroy$ = new Subject<void>();
  private lastActiveElement: HTMLElement | null = null;

  // Mobile layout state
  mobileMenuOpen = signal<boolean>(false);

  @ViewChild('lightbox') private lightboxRef?: ElementRef<HTMLElement>;

  // Scroll spy tracking
  private scrollSpyObserver: IntersectionObserver | null = null;
  private bottomScrollListener: (() => void) | null = null;
  private isScrollingToTarget = false;
  private currentNavigationToken = 0;

  // Delegated article image interactions (click/keyboard to open the lightbox)
  private articleEl: HTMLElement | null = null;

  constructor() {
    // Debounce search so a full preload + scan doesn't run on every keystroke.
    toObservable(this.searchQuery)
      .pipe(debounceTime(200), takeUntilDestroyed())
      .subscribe(() => this.runSearch());
  }

  ngOnInit() {
    this.loadManifest().then(() => {
      // Listen to slug parameter changes
      this.route.paramMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((params) => {
          const slug = params.get('slug') || this.DEFAULT_SLUG;
          this.navigateToSlug(slug);
        });
    });
  }

  ngAfterViewInit() {
    this.setupScrollSpy();

    // #article is static in the template (only its innerHTML changes), so we
    // attach the image-activation listeners once and let them delegate to the
    // images rendered on each navigation.
    this.articleEl = this.elementRef.nativeElement.querySelector('#article');
    this.articleEl?.addEventListener('click', this.onArticleActivate);
    this.articleEl?.addEventListener('keydown', this.onArticleActivate);
  }

  ngOnDestroy() {
    this.navigationDestroy$.next();
    this.navigationDestroy$.complete();
    this.scrollSpyObserver?.disconnect();
    if (this.bottomScrollListener) {
      window.removeEventListener('scroll', this.bottomScrollListener);
    }
    if (this.articleEl) {
      this.articleEl.removeEventListener('click', this.onArticleActivate);
      this.articleEl.removeEventListener('keydown', this.onArticleActivate);
    }
    this.cleanupObjectUrls();
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  // --- Keyboard Shortcuts ---
  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent) {
    // Escape key to close lightbox
    if (event.key === 'Escape' && this.lightboxOpen()) {
      this.closeLightbox();
      event.preventDefault();
      return;
    }
    // Ctrl+K or Cmd+K to focus search input
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      const searchInput = this.elementRef.nativeElement.querySelector('#search-input');
      if (searchInput) {
        searchInput.focus();
      }
    }
  }

  toggleTheme() {
    this.themeService.toggle();
  }

  // --- Fetch Manifest ---
  private async loadManifest() {
    try {
      const data = await this.content.loadManifest();
      if (data) {
        // Add collapsed property
        data.sections = data.sections.map((section) => ({
          ...section,
          collapsed: false,
        }));
        this.manifest.set(data);

        // Build flat items list for navigation without mutating the manifest's
        // section items — each flat entry is a copy carrying its section title.
        const list: MenuItem[] = data.sections.flatMap((section) =>
          section.items.map((item) => ({ ...item, sectionTitle: section.title }))
        );
        this.flatItems.set(list);
      }
    } catch (err) {
      console.error('Could not load user manuals manifest:', err);
      this.currentArticleHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(
          `<div class="loading"><p>Kon de inhoudsopgave niet laden.</p></div>`
        )
      );
    }
  }

  // --- Fetch Content & Render ---
  private async navigateToSlug(slug: string) {
    // Cancel any in-flight media/image requests from the previous slug
    this.navigationDestroy$.next();

    const token = ++this.currentNavigationToken;
    this.currentSlug.set(slug);
    this.showSearchResults.set(false);
    this.searchQuery.set('');

    // Clean up previous page's object URLs to prevent leaks
    this.cleanupObjectUrls();

    // Update Breadcrumbs
    this.updateBreadcrumbs(slug);

    let content = this.content.getCached(slug);
    let loadFailed = false;
    if (!content) {
      // Remove fade-in class and show spinner only when loading from network
      const article = this.elementRef.nativeElement.querySelector('#article');
      if (article) {
        article.classList.remove('fade-in');
      }

      this.currentArticleHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(
          `<div class="loading"><div class="loading-spinner"></div><p>Laden...</p></div>`
        )
      );
      this.outlineItems.set([]);

      try {
        content = (await this.content.getArticle(slug)) ?? undefined;
      } catch (err) {
        console.error(`Failed to load content for slug: ${slug}`, err);
        loadFailed = true;
      }
    } else {
      // If content is cached, remove fade-in class immediately to prepare for smooth transition
      const article = this.elementRef.nativeElement.querySelector('#article');
      if (article) {
        article.classList.remove('fade-in');
      }
    }

    if (token !== this.currentNavigationToken) {
      return;
    }

    if (content) {
      const processedHtml = this.postProcessContent(content.html);
      this.currentArticleHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(processedHtml)
      );
    } else if (loadFailed) {
      this.currentArticleHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(
          `<h1>Verbindingsfout</h1><p>Kon de handleiding niet laden vanwege een netwerkfout. Probeer het opnieuw.</p>`
        )
      );
    } else {
      this.currentArticleHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(
          `<h1>Pagina niet gevonden</h1><p>De handleiding voor dit onderwerp is nog niet beschikbaar.</p>`
        )
      );
    }

    // Run post-render work deterministically after the new article HTML has
    // been committed to the DOM, instead of guessing with a setTimeout.
    afterNextRender(
      () => {
        // Bail if a newer navigation has superseded this one.
        if (token !== this.currentNavigationToken) return;

        const articleElement =
          this.elementRef.nativeElement.querySelector('#article');
        if (articleElement) {
          // Trigger reflow to restart CSS animation
          void (articleElement as HTMLElement).offsetHeight;
          articleElement.classList.add('fade-in');
        }
        if (content) {
          this.buildPageOutline();
          this.observeHeadings();
          this.scrollToHashFragment();
          this.loadImages();
        } else {
          this.outlineItems.set([]);
        }
      },
      { injector: this.injector }
    );

    // Scroll page to top on main content load
    window.scrollTo({ top: 0 });
  }

  // --- Breadcrumbs Helper ---
  private updateBreadcrumbs(slug: string) {
    if (slug === this.DEFAULT_SLUG) {
      this.breadcrumbs.set([]);
      return;
    }
    const item = this.flatItems().find((i) => i.slug === slug);
    if (item) {
      this.breadcrumbs.set([item.sectionTitle || '', item.title]);
    } else {
      this.breadcrumbs.set([]);
    }
  }

  // --- Post-Process HTML Elements ---
  private postProcessContent(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // 1. Callout blocks styling
    const blockquotes = doc.querySelectorAll('blockquote');
    blockquotes.forEach((bq) => {
      // textContent has markdown emphasis already rendered away (** became
      // <strong>), so match on the leading label text only.
      const text = bq.textContent?.trim() || '';
      if (text.startsWith('Tip:')) {
        bq.classList.add('callout-tip');
      } else if (text.startsWith('Let op:')) {
        bq.classList.add('callout-warning');
      } else if (text.startsWith('Waarschuwing:')) {
        bq.classList.add('callout-danger');
      }
    });

    // 2. Set ID on headings for deep linking, de-duplicating collisions
    const seenIds = new Set<string>();
    const headings = doc.querySelectorAll('h2, h3, h4');
    headings.forEach((heading) => {
      const base = this.slugify(heading.textContent || '');
      let id = base;
      let counter = 2;
      while (seenIds.has(id)) {
        id = `${base}-${counter++}`;
      }
      seenIds.add(id);
      heading.id = id;
    });

    // 3. Store relative image paths in data-src attribute for lazy authenticated
    //    loading, and make images keyboard-operable so they can open the lightbox.
    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        img.setAttribute('data-src', src);
        img.removeAttribute('src');
      }

      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      if (!img.getAttribute('aria-label')) {
        const alt = img.getAttribute('alt');
        img.setAttribute(
          'aria-label',
          alt ? `Afbeelding vergroten: ${alt}` : 'Afbeelding vergroten'
        );
      }
    });

    return doc.body.innerHTML;
  }

  private loadImages() {
    const article = this.elementRef.nativeElement.querySelector('#article');
    if (!article) return;

    const images = article.querySelectorAll('img[data-src]');
    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('data-src');
      if (src) {
        this.content
          .getMedia(src)
          .pipe(
            takeUntil(this.navigationDestroy$),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: (blob) => {
              const objectUrl = URL.createObjectURL(blob);
              this.objectUrls.push(objectUrl);

              // Pre-load image in background to get natural dimensions
              const tempImg = new Image();
              tempImg.onload = () => {
                img.style.aspectRatio = `${tempImg.naturalWidth} / ${tempImg.naturalHeight}`;
                img.src = objectUrl;
                img.classList.remove('img-loading');
                img.classList.add('img-loaded');
              };
              tempImg.onerror = () => {
                img.remove();
              };
              tempImg.src = objectUrl;
            },
            error: () => {
              // Image does not exist in media storage; silently remove the placeholder element
              img.remove();
            },
          });
      }
    });
  }

  private cleanupObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }

  // Delegated handler (arrow fn so the bound reference is stable for add/remove
  // EventListener). Opens the lightbox when an article image is clicked or
  // activated with Enter/Space while focused.
  private onArticleActivate = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() !== 'img') return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const img = target as HTMLImageElement;
    this.openLightbox(img.src, img.alt);
  };

  /**
   * Close the lightbox when the backdrop itself is clicked, or when the enlarged
   * image is clicked (a mouse convenience). Clicks on other content (e.g. the
   * caption) are ignored. Handled here rather than via a per-element (click) so
   * the markup stays accessible without extra interactive elements.
   */
  onLightboxClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      target === event.currentTarget ||
      target.classList.contains('lightbox-img')
    ) {
      this.closeLightbox();
    }
  }

  openLightbox(src: string, alt: string | null) {
    this.lastActiveElement = document.activeElement as HTMLElement;
    this.lightboxImage.set(src);
    this.lightboxAlt.set(alt);
    this.lightboxOpen.set(true);

    // Calculate scrollbar width to prevent horizontal layout shift/reflow
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog so Escape works at the element and screen
    // readers enter the modal.
    afterNextRender(() => this.lightboxRef?.nativeElement.focus(), {
      injector: this.injector,
    });
  }

  closeLightbox() {
    this.lightboxOpen.set(false);

    // Restore overflow and padding after the 300ms transition completes
    setTimeout(() => {
      if (!this.lightboxOpen()) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    }, 300);

    // Restore focus immediately for keyboard users
    if (this.lastActiveElement) {
      this.lastActiveElement.focus();
      this.lastActiveElement = null;
    }
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 60);
  }

  // --- Outline generator ---
  private buildPageOutline() {
    const article = this.elementRef.nativeElement.querySelector('#article');
    if (!article) return;

    const headings = Array.from(article.querySelectorAll('h2, h3')) as HTMLElement[];
    const items = headings.map((h) => ({
      id: h.id,
      text: h.textContent || '',
      depth: h.tagName.toLowerCase() === 'h3' ? 3 : 2,
    }));

    this.outlineItems.set(items);
    if (items.length > 0) {
      this.activeOutlineId.set(items[0].id);
    }
  }

  scrollToHeading(headingId: string) {
    const target = document.getElementById(headingId);
    if (target) {
      this.isScrollingToTarget = true;
      this.activeOutlineId.set(headingId);

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Update hash in history keeping the current pathname (with tenant)
      history.pushState(null, '', `${window.location.pathname}#${headingId}`);

      setTimeout(() => {
        this.isScrollingToTarget = false;
      }, 800);
    }
  }

  private scrollToHashFragment() {
    const hash = window.location.hash;
    if (!hash) return;

    const parts = hash.split('#');
    // Handle both #heading and legacy #docs/slug#heading formats
    let targetHeading = '';
    if (parts.length > 2) {
      targetHeading = parts[2];
    } else if (parts.length === 2) {
      if (!parts[1].includes('/')) {
        targetHeading = parts[1];
      } else {
        targetHeading = parts[1].split('/')[1] || '';
      }
    }

    if (targetHeading) {
      const heading = document.getElementById(targetHeading);
      if (heading) {
        // Called from the afterNextRender hook, so the heading is already in
        // the DOM — scroll directly without a timing guess.
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // --- Scroll Spy Implementation ---
  private setupScrollSpy() {
    // Create the observer once; targets are (re)attached per navigation by
    // observeHeadings(). The rootMargin defines a detection band near the top
    // of the viewport: a heading becomes active while it sits in that band.
    this.scrollSpyObserver = new IntersectionObserver(
      (entries) => {
        if (this.isScrollingToTarget) return;

        for (const entry of entries) {
          if (entry.isIntersecting && entry.target.id) {
            this.activeOutlineId.set(entry.target.id);
          }
        }
      },
      { rootMargin: '-120px 0px -66% 0px', threshold: 0 }
    );

    // Trailing sections can be too short to ever reach the detection band when
    // the page is scrolled fully down, so the observer alone would never mark
    // them active. This passive listener handles only the bottom case with an
    // O(1) check (no per-heading layout reads), forcing the last heading active.
    this.bottomScrollListener = () => {
      if (this.isScrollingToTarget) return;

      const docEl = document.documentElement;
      const scrollable = docEl.scrollHeight > window.innerHeight + 1;
      if (!scrollable) return;

      const atBottom =
        window.innerHeight + window.scrollY >= docEl.scrollHeight - 20;
      if (!atBottom) return;

      const article = this.elementRef.nativeElement.querySelector('#article');
      const headings = article?.querySelectorAll('h2, h3');
      if (headings?.length) {
        this.activeOutlineId.set(headings[headings.length - 1].id);
      }
    };
    window.addEventListener('scroll', this.bottomScrollListener, {
      passive: true,
    });
  }

  private observeHeadings() {
    if (!this.scrollSpyObserver) return;

    this.scrollSpyObserver.disconnect();
    const article = this.elementRef.nativeElement.querySelector('#article');
    if (!article) return;

    const headings = article.querySelectorAll('h2, h3');
    headings.forEach((heading: Element) =>
      this.scrollSpyObserver!.observe(heading)
    );
  }

  // --- Sidebar navigation ---
  selectSlug(slug: string) {
    this.router.navigate(['/docs', slug]);
    this.closeMobileMenu();
  }

  toggleSection(section: NavSection) {
    section.collapsed = !section.collapsed;
  }

  // --- Footer Navigation (Prev/Next) ---
  get prevItem(): MenuItem | null {
    const list = this.flatItems();
    const idx = list.findIndex((i) => i.slug === this.currentSlug());
    return idx > 0 ? list[idx - 1] : null;
  }

  get nextItem(): MenuItem | null {
    const list = this.flatItems();
    const idx = list.findIndex((i) => i.slug === this.currentSlug());
    return idx !== -1 && idx < list.length - 1 ? list[idx + 1] : null;
  }

  // --- Search functionality ---
  preloadOnSearchFocus() {
    this.content.preloadAll(this.flatItems());
  }

  private async runSearch() {
    const query = this.searchQuery().trim();
    if (!query || query.length < 2) {
      this.showSearchResults.set(false);
      this.searchResults.set([]);
      return;
    }

    const results = await this.content.search(this.flatItems(), query);
    this.searchResults.set(results);
    this.showSearchResults.set(true);
  }

  highlightSearchTerm(text: string): string {
    // HTML-escape the excerpt first so raw markdown characters (< & etc.) can't
    // render as markup when this string is bound via innerHTML.
    const escapedText = this.escapeHtml(text);
    const query = this.searchQuery().trim();
    if (!query) return escapedText;

    // Escape the query the same way so it matches against the escaped excerpt.
    const escapedQuery = this.escapeRegExp(this.escapeHtml(query));
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return escapedText.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // --- Mobile Sidebar Controls ---
  toggleMobileMenu() {
    this.mobileMenuOpen.update((val) => !val);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  // --- SVGs mapping ---
  private readonly iconCache = new Map<string, SafeHtml>();

  getIconSvg(slug: string): SafeHtml {
    let safe = this.iconCache.get(slug);
    if (!safe) {
      const svgStr = NAV_ICONS[slug] || NAV_ICON_FALLBACK;
      safe = this.sanitizer.bypassSecurityTrustHtml(svgStr);
      this.iconCache.set(slug, safe);
    }
    return safe;
  }
}
